import type { components } from './generated/types';

type ErrorResponse = components['schemas']['ErrorResponse'];

/**
 * Unwraps a list of items from the API. Each item in a list may success or fail. If any failed,
 * this will throw an error. Otherwise you will receive back the same list but with the data object
 * unwrapped.
 *
 * @throws {Error} If any response in the array failed
 * @returns The data property of each item in the list
 */
export function unwrapList<T>(
  responses: (ErrorResponse | { success: true; data: T })[],
): T[] {
  return responses.map((response) => {
    if ('success' in response && !response.success) {
      throw new Error(response.message ?? 'Request failed');
    }
    return 'data' in response ? response.data : response;
  });
}

/**
 * Unwraps all successful responses from the the API. Each item in a list may success or fail. If any failed,
 * this silently ignore them. You will receive back the subset of the list that succeeded, each with
 * its data property unwrapped. Any failures are silently ignored.
 *
 * If you want to act on failures (like logging them), you should use splitResponses instead.
 *
 * @returns Array containing only the successful response data
 */
export function unwrapListIgnoreErrors<T>(
  responses: (ErrorResponse | { success: true; data: T })[],
): T[] {
  return responses
    .filter(
      (response): response is { success: true; data: T } =>
        'success' in response && response.success && 'data' in response,
    )
    .map((response) => response.data);
}

/**
 * Splits a list of API responses into two lists: one for successes and one for failures. Successes
 * will have their data property unwrapped. Errors are returned as-is.
 *
 * Use this helper if you care about the list of failures; otherwise unwrapList or
 * unwrapListIgnoreErrors are higher level and more direct.
 *
 * @param responses The list of API responses to split
 *
 * @returns
 */
export function splitResponses<T>(
  responses: (ErrorResponse | { success: true; data: T })[],
): {
  successes: T[];
  failures: ErrorResponse[];
} {
  const successes: T[] = [];
  const failures: ErrorResponse[] = [];

  for (const response of responses) {
    if ('success' in response && response.success) {
      successes.push('data' in response ? response.data : (response as T));
    } else {
      failures.push(response);
    }
  }

  return { successes, failures };
}
